<script setup>
import axios from 'axios';
import { ref, computed, watch } from 'vue';
import { Head, router, useForm, usePage } from '@inertiajs/vue3';
import { PhUserCirclePlus, PhCamera } from "@phosphor-icons/vue";
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import Table from '@pages/Shared/ui/Table.vue';
import Spinner from '@pages/Shared/ui/Spinner.vue';
import Modal from '@pages/Shared/Modal.vue';
import Prompt from '@pages/Shared/Prompt.vue';
import Input from '@pages/Shared/form/Input.vue';
import Select from '@pages/Shared/form/Select.vue';
import MultiSelect from '@pages/Shared/form/MultiSelect.vue';
import Textarea from '@pages/Shared/form/Textarea.vue';
import { flashToast } from "@/toast.js";
import userMale from '@/../images/user-male.png';
import userFemale from '@/../images/user-female.png';
import { capitalize } from '@/helpers.js';

const props = defineProps({
    staff:    Object,
    roles:    Array,
    classes:  Array,
    subjects: Array,
    filters:  Object,
});

const openModal   = ref(false);
const modalStatus = ref(null);
const modalTitle  = ref('');
const showPrompt  = ref(false);
const fileInput   = ref(null);

const filters = ref({ search: null, gender: null, role: null, status: null, ...props.filters });

const form = useForm({
    _method:  null,
    id:       null,
    avatar:   null,
    preview:  null,
    name:     '',
    email:    '',
    gender:   null,
    dob:      '',
    phone:    '',
    address:  '',
    status:   null,
    roles:    [],
    classes:  [],
    subjects: [],
});

const columns = [
    { key: 'index',           label: '#',         type: 'index'   },
    { key: 'id',              label: 'ID'                          },
    { key: 'avatar',          label: 'Avatar',    type: 'slot'    },
    { key: 'name',            label: 'Name'                        },
    { key: 'email',           label: 'Email'                       },
    { key: 'gender',          label: 'Gender'                      },
    { key: 'phone',           label: 'Phone'                       },
    { key: 'address',         label: 'Address'                     },
    { key: 'roles',           label: 'Role(s)',   type: 'slot'    },
    { key: 'classes',         label: 'Class(es)', type: 'slot'    },
    { key: 'subjects',        label: 'Subject(s)',type: 'slot'    },
    { key: 'status',          label: 'Status',    type: 'slot'    },
    { key: 'actions',         label: '',          type: 'actions' },
];

const roles = computed(() =>
    (props.roles ?? []).map(r => ({ label: capitalize(r.name), value: r.id }))
);
const classRooms = computed(() =>
    (props.classes ?? []).map(c => ({ label: capitalize(c.name), value: c.id }))
);
const subjects = computed(() =>
    (props.subjects ?? []).map(s => ({ label: capitalize(s.name), value: s.id }))
);

const isFormMaster = computed(() => {
    const role = roles.value.find(r => r.label.toLowerCase().includes('form master'));
    return role ? form.roles.includes(role.value) : false;
});
const isClassTeacher = computed(() => {
    const role = roles.value.find(r => r.label.toLowerCase().includes('class teacher'));
    return role ? form.roles.includes(role.value) : false;
});

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true });

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    );
    router.get('/admin/staff', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    });
};

const handleSearch = (value) => { filters.value.search = value; applyFilters(); };
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters(); };

const setFormData = (selected) => {
    form.id       = selected.id;
    form.avatar   = selected.avatar;
    form.name     = selected.name;
    form.email    = selected.email;
    form.gender   = selected.gender;
    form.dob      = selected.dob;
    form.phone    = selected.phone;
    form.address  = selected.address;
    form.status   = selected.status;
    form.roles    = selected.roles?.map(r => r.id) ?? [];
    form.classes  = selected.class_rooms?.map(c => c.id) ?? [];
    form.subjects = selected.subjects?.map(s => s.id) ?? [];
    form.preview  = null;
};

const editStaff = (id) => {
    const selected = props.staff.data.find(s => s.id === id);
    if (!selected) return;
    setFormData(selected);
    modalTitle.value  = 'Edit Staff';
    modalStatus.value = 'edit';
    openModal.value   = true;
};

const viewStaff = (id) => {
    const selected = props.staff.data.find(s => s.id === id);
    if (!selected) return;
    setFormData(selected);
    modalTitle.value  = 'View Staff';
    modalStatus.value = 'view';
    openModal.value   = true;
};

const deleteStaff = (id) => { form.id = id; showPrompt.value = true; };

const confirmDelete = () => {
    form.delete(`/admin/staff/${encodeURIComponent(form.id)}`, {
        onSuccess: (page) => {
            showPrompt.value = false;
            form.clearErrors();
            form.reset();
            flashToast(page.props.flash);
        }
    });
};

const openAddStaffModal = async () => {
    form.reset();
    modalTitle.value  = 'Add Staff';
    modalStatus.value = 'create';

    try {
        const res = await axios.post('/admin/staff/get-id');
        form.id = res.data.id;
    } catch {
        form.id = null;
    }

    openModal.value = true;
};

const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.preview) URL.revokeObjectURL(form.preview);
    form.avatar  = file;
    form.preview = URL.createObjectURL(file);
};

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => {
            if (form.preview) URL.revokeObjectURL(form.preview);
            form.clearErrors();
            form.reset();
            openModal.value = false;
            flashToast(page.props.flash);
        }
    };
    if (modalStatus.value === 'edit') {
        form._method = 'put';
        form.post(`/admin/staff/${encodeURIComponent(form.id)}`, options);
    } else {
        form.post('/admin/staff', options);
    }
};

const handleModalClose = () => {
    if (form.preview) URL.revokeObjectURL(form.preview);
    modalStatus.value = null;
    modalTitle.value  = null;
    openModal.value   = false;
    form.clearErrors();
    form.reset();
};

const page = usePage();
const can  = computed(() => page.props.can || {});
</script>

<template>
    <Layout>
        <Head title="Staff Management" />

        <Prompt
            title="staff"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            @close="handleModalClose"
        >
            <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

                <!-- Avatar -->
                <div class="relative w-28 h-28 mx-auto my-4">
                    <img
                        :src="form.preview
                            ? form.preview
                            : (form.avatar
                                ? `/storage/${form.avatar}`
                                : (form.gender === 'Male' ? userMale : userFemale))"
                        class="w-full h-full object-cover rounded-full ring-4 ring-gray-100"
                        alt="Staff avatar"
                    />
                    <span
                        v-if="modalStatus !== 'view'"
                        class="absolute bottom-0 right-0 p-1.5 rounded-full text-white cursor-pointer transition"
                        :class="modalStatus === 'edit'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-primary hover:bg-primary-light'"
                        @click="fileInput?.click()"
                    >
                        <PhCamera :size="16" />
                    </span>
                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        class="hidden"
                        @change="handleFileSelected"
                    />
                </div>

                <Input v-model="form.name" label="Full Name" placeholder="Enter full name"
                    :disabled="modalStatus === 'view'" :error="form.errors.name" class="mb-4" />

                <Input v-model="form.email" label="Email" type="email" placeholder="Email"
                    :disabled="modalStatus === 'view'" :error="form.errors.email" class="mb-4" />

                <Select label="Gender" v-model="form.gender" :options="['Male', 'Female']"
                    placeholder="Select Gender" :disabled="modalStatus === 'view'"
                    :error="form.errors.gender" class="mb-4" />

                <Input v-model="form.dob" type="date" label="Date of Birth"
                    :disabled="modalStatus === 'view'" :error="form.errors.dob" class="mb-4" />

                <Input v-model="form.phone" type="text" label="Phone" placeholder="Phone Number"
                    :disabled="modalStatus === 'view'" :error="form.errors.phone" class="mb-4" />

                <Textarea label="Address" v-model="form.address" placeholder="Enter address..."
                    :rows="2" :disabled="modalStatus === 'view'"
                    :error="form.errors.address" class="mb-4" />

                <MultiSelect v-model="form.roles" :options="roles" label="Roles"
                    placeholder="Select role" class="mb-4" :disabled="modalStatus === 'view'" />

                <!-- <Select v-if="isFormMaster" label="Class Room" v-model="form.class"
                    :options="classRooms" placeholder="Select class"
                    :disabled="modalStatus === 'view'" :error="form.errors.class" class="mb-4" /> -->
                <MultiSelect v-if="isFormMaster"  label="Class Room(s)"  v-model="form.classes" 
                    :options="classRooms"  placeholder="Select class(es)" :disabled="modalStatus === 'view'" 
                    :error="form.errors.classes"  class="mb-4" 
                />

                <MultiSelect v-if="isClassTeacher" v-model="form.subjects" :options="subjects"
                    label="Subjects" placeholder="Select subjects"
                    :disabled="modalStatus === 'view'" :error="form.errors.subjects" class="mb-4" />

                <Select v-if="modalStatus !== 'create'" label="Status" v-model="form.status"
                    :options="['Active', 'Inactive']" placeholder="Select status"
                    :disabled="modalStatus === 'view'" :error="form.errors.status" class="mb-4" />

                <Input v-model="form.id" type="text" label="Staff ID"
                    :disabled="true" :error="form.errors.id" class="mb-4" />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                        transition flex justify-center items-center gap-2"
                    :class="modalStatus === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary hover:bg-primary-light'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update Staff' : 'Add Staff' }}
                </button>
            </form>
        </Modal>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Staff</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all staff members</p>
            </div>
            <button
                v-if="can.createStaff"
                @click="openAddStaffModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhUserCirclePlus :size="20" />
                Add Staff
            </button>
        </div>

        <Table
            :data="staff"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'gender', label: 'Gender',
                    options: [
                        { label: 'All Genders', value: null },
                        { label: 'Male',        value: 'Male' },
                        { label: 'Female',      value: 'Female' },
                    ],
                    value: filters.gender,
                },
                {
                    key: 'role', label: 'Role',
                    options: [{ label: 'All Roles', value: null }, ...roles],
                    value: filters.role,
                },
                {
                    key: 'class', label: 'Class',
                    options: [{ label: 'All Classes', value: null }, ...classRooms],
                    value: filters.class,
                },
                {
                    key: 'status', label: 'Status',
                    options: [
                        { label: 'All Statuses', value: null },
                        { label: 'Active',       value: 'active' },
                        { label: 'Inactive',     value: 'inactive' },
                    ],
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="viewStaff"
            @edit="editStaff"
            @delete="deleteStaff"
        >
            <template #avatar="{ row }">
                <img
                    class="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100"
                    :src="row.avatar
                        ? `/storage/${row.avatar}`
                        : (row.gender === 'Male' ? userMale : userFemale)"
                    :alt="row.name"
                />
            </template>

            <template #roles="{ row }">
                <div class="flex flex-wrap gap-1">
                    <span
                        v-for="role in row.roles" :key="role.id"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                               bg-primary-50 text-primary rounded-md border border-primary-100"
                    >
                        {{ capitalize(role.name) }}
                    </span>
                </div>
            </template>

            <template #classes="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.class_rooms?.length">
                        <span
                            v-for="cls in row.class_rooms" :key="cls.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100"
                        >
                            {{ capitalize(cls.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>

            <template #subjects="{ row }">
                <div class="flex flex-wrap gap-1">
                    <template v-if="row.subjects?.length">
                        <span
                            v-for="subject in row.subjects" :key="subject.id"
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                                bg-purple-50 text-purple-700 rounded-md border border-purple-100"
                        >
                            {{ capitalize(subject.name) }}
                        </span>
                    </template>
                    <span v-else class="text-xs text-gray-400">N/A</span>
                </div>
            </template>

            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="row.status?.toLowerCase() === 'active'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-orange-50 text-orange-600'"
                >
                    <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="row.status?.toLowerCase() === 'active'
                            ? 'bg-green-500' : 'bg-orange-400'"
                    ></span>
                    {{ row.status }}
                </span>
            </template>
        </Table>
    </Layout>
</template>